import React, { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { supportsSignalStrength } from '../utils/deviceTypes';

import { useAPI } from '../Hooks/useAPIs';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { ErrorState, LoadingState } from '../common/States/States';
import {
  getErrorMessage,
  formatBytes,
  getInventoryClientVersion,
  getInventoryDeviceType,
  getInventoryLastUpdated,
  getOnStarLastSeen,
} from './utils';
import { DetailSection, DetailField, JsonAccordion } from './components';

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

const joinDisplayValues = (values, separator = ' / ') => {
  const parts = values.filter(hasDisplayValue).map(String);
  return parts.length ? parts.join(separator) : '';
};

const formatLinkedEntity = (entity, primaryField) => {
  if (!entity) {
    return '';
  }

  const primaryValue = entity?.[primaryField];
  const entityId = entity?.id;

  if (hasDisplayValue(primaryValue) && hasDisplayValue(entityId)) {
    return `${primaryValue} (${entityId})`;
  }

  if (hasDisplayValue(primaryValue)) {
    return String(primaryValue);
  }

  if (hasDisplayValue(entityId)) {
    return String(entityId);
  }

  return '';
};

export const WiviInventoryDetailContent = ({ snapshotId, fallbackData = null, enableFetch = true }) => {
  const { apiCall, isMsalReady } = useAPI();
  const { formatDateTime } = useContext(TimezoneContext);

  const detailQuery = useQuery({
    queryKey: ['wivi-inventory-detail', snapshotId],
    queryFn: async () => {
      const response = await apiCall('GET', `/api/wivi-inventory/${snapshotId}/`);
      return response.data;
    },
    placeholderData: fallbackData,
    enabled: isMsalReady && enableFetch && Boolean(snapshotId),
  });

  const snapshot = detailQuery.data || fallbackData;
  const inventoryDeviceType = getInventoryDeviceType(snapshot);
  const signalValue = supportsSignalStrength(inventoryDeviceType)
    ? snapshot?.status_payload?.signalBars?.value ?? snapshot?.status_signal_bars
    : 'Unavailable';

  if (detailQuery.isLoading && !snapshot) {
    return (
      <Box className="wivi-detail-loading">
        <LoadingState message="Loading snapshot details..." />
      </Box>
    );
  }

  if (detailQuery.isError && !snapshot) {
    return (
      <Box className="wivi-detail-loading">
        <ErrorState
          title="Unable to load snapshot"
          message={getErrorMessage(detailQuery.error, 'The detail endpoint returned an unexpected error.')}
          onRetry={() => detailQuery.refetch()}
        />
      </Box>
    );
  }

  return (
    <Box className="wivi-detail-panel" sx={{ bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.05)', border: '1px solid', borderColor: 'divider' }}>
      {detailQuery.isFetching ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} /> : null}
      {detailQuery.isError ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {getErrorMessage(detailQuery.error, 'Showing list data because the detail request failed.')}
        </Alert>
      ) : null}

      <Stack spacing={5}>
          <DetailSection title="Vehicle & Device Info" icon={<DirectionsCarIcon />}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Vehicle Overview</Typography>
              <Stack spacing={0}>
                <DetailField label="Snapshot ID" value={snapshot?.id} />
                <DetailField label="VIN" value={snapshot?.vin} />
                <DetailField label="Vehicle Name" value={snapshot?.vehicle_name || snapshot?.display_name} />
                <DetailField label="Make / Model" value={joinDisplayValues([snapshot?.make, snapshot?.model])} />
                <DetailField label="Year" value={snapshot?.year} />
                <DetailField label="OnStar Timestamp" value={formatDateTime(getOnStarLastSeen(snapshot))} />
                <DetailField label="Remote Vehicle ID" value={snapshot?.remote_vehicle_id} />
                <DetailField label="Vehicle Version" value={snapshot?.remote_vehicle_version} />
                <DetailField label="VIN Read Only" value={snapshot?.vin_read_only} isStatus />
              </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Device Overview</Typography>
              <Stack spacing={0}>
                <DetailField label="Remote Device ID" value={snapshot?.remote_device_id} />
                <DetailField label="Device Name" value={snapshot?.remote_device_name} />
                <DetailField label="Device Type" value={inventoryDeviceType} />
                <DetailField label="Serial" value={snapshot?.remote_device_serial} />
                <DetailField label="Client Version" value={getInventoryClientVersion(snapshot)} />
                <DetailField label="Device Version" value={snapshot?.remote_device_version} />
                <DetailField label="Licensed" value={snapshot?.remote_device_licensed} isStatus />
                <DetailField label="SMS" value={snapshot?.remote_device_sms} />
              </Stack>
              </Grid>
            </Grid>
          </DetailSection>

          <DetailSection title="Fleet, Org & Status Info" icon={<BusinessIcon />}>
             <Grid container spacing={4}>
               <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Organization Setup</Typography>
              <Stack spacing={0}>
                <DetailField label="Fleet" value={snapshot?.remote_fleet_name} />
                <DetailField label="Fleet ID" value={snapshot?.remote_fleet_id} />
                <DetailField label="Organization" value={snapshot?.remote_organization_name} />
                <DetailField label="Organization ID" value={snapshot?.remote_organization_id} />
                <DetailField label="Fleet Deleted" value={snapshot?.remote_fleet_delete_flag} isStatus />
                <DetailField label="Org Deleted" value={snapshot?.remote_organization_delete_flag} isStatus />
              </Stack>
              </Grid>
               <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Status & Storage</Typography>
              <Stack spacing={0}>
                <DetailField label="Total Storage" value={formatBytes(snapshot?.status_payload?.totalStorage?.value ?? snapshot?.status_total_storage)} />
                <DetailField label="Remaining Storage" value={formatBytes(snapshot?.status_payload?.remainingStorage?.value ?? snapshot?.status_remaining_storage)} />
                <DetailField label="Network Type" value={snapshot?.status_payload?.networkType?.value ?? snapshot?.status_network_type} />
                <DetailField label="Signal Bars" value={signalValue} />
                <DetailField label="Last Updated" value={formatDateTime(getInventoryLastUpdated(snapshot))} />
                <DetailField label="Synced At" value={formatDateTime(snapshot?.synced_at)} />
                <DetailField label="Online" value={snapshot?.status_payload?.online?.value ?? snapshot?.status_online} isStatus />
                <DetailField label="Logging" value={snapshot?.status_payload?.logging?.value ?? snapshot?.status_logging} isStatus />
                <DetailField label="Running" value={snapshot?.status_payload?.running?.value ?? snapshot?.status_running} isStatus />
              </Stack>
              </Grid>
            </Grid>
          </DetailSection>

          <DetailSection title="Scripts & Version Info" icon={<CodeIcon />}>
            <Grid container spacing={4}>
               <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Script & Local Mapping</Typography>
                <Stack spacing={0}>
                  <DetailField label="Script" value={snapshot?.script_name} />
                  <DetailField label="Script ID" value={snapshot?.script_id} />
                  <DetailField label="Download File ID" value={snapshot?.script_download_file_id} />
                  <DetailField label="Local Vehicle" value={formatLinkedEntity(snapshot?.local_vehicle, 'vin')} />
                  <DetailField label="Local Device" value={formatLinkedEntity(snapshot?.local_device, 'pi')} />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>Device Versions</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(snapshot?.device_versions || {}).length > 0 ? (
                    Object.entries(snapshot?.device_versions || {}).map(([key, value]) => (
                      <Chip key={key} label={`${key}: ${value}`} size="small" variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No device version metadata returned.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DetailSection>

        <DetailSection title="Payload Data" icon={<Inventory2OutlinedIcon />}>
          <Stack spacing={0}>
            <JsonAccordion title="Status Payload" value={snapshot?.status_payload || {}} defaultExpanded />
            <JsonAccordion title="Script Payload" value={snapshot?.script_payload || {}} />
            <JsonAccordion title="Last Upload Payload" value={snapshot?.last_upload_payload || {}} />
            <JsonAccordion title="Local Vehicle" value={snapshot?.local_vehicle || {}} />
            <JsonAccordion title="Local Device" value={snapshot?.local_device || {}} />
            <JsonAccordion title="Raw Vehicle Payload" value={snapshot?.raw_vehicle_payload || {}} />
            <JsonAccordion title="Raw Device Payload" value={snapshot?.raw_device_payload || {}} />
            <JsonAccordion title="Merged Payload" value={snapshot?.merged_payload || {}} />
          </Stack>
        </DetailSection>
      </Stack>
    </Box>
  );
};
